import React, { useEffect, useState } from 'react';
import { motion, useAnimation, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface DomainFinalCallSectionProps {
  domainName: string;
  favoritesCount?: number;
  onPrimary: () => void;
  onSecondary?: () => void;
}

export default function DomainFinalCallSection({
  domainName,
  favoritesCount,
  onPrimary,
  onSecondary,
}: DomainFinalCallSectionProps) {
  const reduceMotion = useReducedMotion();

  const CTA_DEMO_CLASSES =
    'inline-flex items-center justify-center gap-2 rounded-2xl font-poppins tracking-wide font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0000FF] min-h-[52px] px-16 py-5 text-xl relative overflow-hidden transition-transform transition-colors duration-300 ease-out select-none text-white hover:text-white active:text-white';
  const shimmerBackground = 'linear-gradient(180deg, #F3F7FF 0%, #2F62FF 45%, #0037FF 100%)';
  const borderGlowGradient =
    'linear-gradient(135deg, rgba(102, 178, 255, 0.95), rgba(160, 118, 255, 0.9), rgba(102, 178, 255, 0.95))';
  const MotionLink = motion.create(Link);

  const shimmerControls = useAnimation();
  const textControls = useAnimation();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (reduceMotion) return;
    shimmerControls.set({ opacity: 0, x: '-140%' });
    textControls.set({
      y: 0,
      textShadow: '0px 0px 0px rgba(255,255,255,0)',
      scale: 1,
    });
  }, [reduceMotion, shimmerControls, textControls]);

  useEffect(() => {
    if (reduceMotion) return;
    if (isHovered) {
      shimmerControls.start({
        opacity: 1,
        x: '140%',
        transition: {
          duration: 1.15,
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'mirror',
        },
      });
      textControls.start({
        y: 0,
        textShadow: '0px 10px 35px rgba(53,121,255,0.55)',
        scale: 1.08,
        transition: { duration: 0.22, ease: 'easeOut' },
      });
    } else {
      shimmerControls.start({
        opacity: 0,
        x: '-140%',
        transition: { duration: 0.28, ease: 'easeOut' },
      });
      textControls.start({
        y: 0,
        textShadow: '0px 0px 0px rgba(0,0,0,0)',
        scale: 1,
        transition: { duration: 0.18, ease: 'easeOut' },
      });
    }
  }, [isHovered, reduceMotion, shimmerControls, textControls]);

  const handleHoverStart = () => {
    if (reduceMotion) return;
    setIsHovered(true);
  };

  const handleHoverEnd = () => {
    if (reduceMotion) return;
    setIsHovered(false);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const favoriteLine =
    typeof favoritesCount === 'number' && favoritesCount > 0
      ? `Favorited by ${favoritesCount} other buyers.`
      : null;

  return (
    <section className="apple-hero-bg text-white py-24 relative overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="max-w-4xl mx-auto px-4 md:px-6 text-center space-y-8 relative z-10"
      >
        <div className="space-y-3">
          <h2 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg text-white">
            Don’t Let Your Competitor Own {domainName}
          </h2>
          <p className="text-3xl md:text-4xl text-white/90 leading-relaxed max-w-4xl mx-auto font-normal mb-8">
            This is a one-of-one asset. Once it’s sold, it’s gone forever. Secure it now and, if you choose, let my Launch Studio turn it into a fully-booked brand.
          </p>
        </div>

        <div className="flex justify-center pb-32">
          <MotionLink
            to="#"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onPrimary();
            }}
            className={CTA_DEMO_CLASSES}
            style={{
              backgroundImage: `${shimmerBackground}, ${borderGlowGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              backgroundSize: '100% 100%, 200% 200%',
              backgroundPosition: '0% 0%, 0% 50%',
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: 'transparent',
              boxShadow: '0 14px 34px rgba(14, 48, 140, 0.35)',
              color: '#FFFFFF',
            }}
            whileHover={
              reduceMotion
                ? {}
                : {
                    scale: 1.04,
                    y: -2,
                    backgroundPosition: '0% 0%, 100% 45%',
                    boxShadow: '0 26px 58px rgba(30, 88, 210, 0.5)',
                    filter: 'drop-shadow(0px 18px 42px rgba(41, 87, 209, 0.35))',
                    color: '#FFFFFF',
                  }
            }
            whileTap={{ scale: 0.97, color: '#FFFFFF' }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            onHoverStart={handleHoverStart}
            onHoverEnd={handleHoverEnd}
            onFocus={handleHoverStart}
            onBlur={handleHoverEnd}
          >
            <motion.span
              className="relative z-20 text-white inline-flex items-center justify-center"
              style={{ color: '#FFFFFF', display: 'inline-flex', transformOrigin: '50% 50%' }}
              animate={reduceMotion ? undefined : textControls}
            >
              Secure This Domain Now
            </motion.span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-2xl opacity-70"
              style={{
                background:
                  'radial-gradient(110% 110% at 50% 12%, rgba(135, 196, 255, 0.45) 0%, rgba(7, 25, 90, 0) 70%)',
                filter: 'blur(8px)',
                mixBlendMode: 'screen',
              }}
            />
            {!reduceMotion && (
              <motion.span
                aria-hidden="true"
                className="pointer-events-none absolute inset-[-35%] z-10 opacity-0 mix-blend-screen bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0.65)_45%,rgba(255,255,255,0.85)_52%,transparent_75%)]"
                initial={{ x: '-140%', opacity: 0 }}
                animate={shimmerControls}
              />
            )}
          </MotionLink>
        </div>

        {/* Optional social proof & support removed per request */}
      </motion.div>
    </section>
  );
}

